import React from 'react';
import { LIMITS } from '../../../shared/fieldTypes';
import ToggleRow from '../../fields/ToggleRow';
import ListEditor from '../ListEditor';
import { Hint, TextField } from '../parts';
import Stepper from '../Stepper';
import { editorPropTypes } from './LayoutEditors';

// Type-specific settings for answer fields (plan §6 A2 "Type editors").
// Each gets { field, update(patch), setConfig(key, value) }; the common
// label / placeholder / help / required / pre-fill rows live in
// FieldProperties.

const OptionsEditor = ({ field, setConfig }) => (
  <ListEditor
    label="Options"
    noun="option"
    items={field.config.options}
    min={0}
    max={LIMITS.options}
    onChange={(options) => setConfig('options', options)}
  />
);

OptionsEditor.propTypes = editorPropTypes;

// Dropdown and radio: the options plus the "Other" switch. (A checkbox list
// has no Other: its answer is a list of options.)
const ChoiceEditor = ({ field, update, setConfig }) => (
  <>
    <OptionsEditor field={field} update={update} setConfig={setConfig} />
    <ToggleRow
      label="Allow “Other”"
      sub="Adds an Other choice with a box to type in"
      on={field.config.allow_other === true}
      onChange={(on) => setConfig('allow_other', on)}
    />
  </>
);

ChoiceEditor.propTypes = editorPropTypes;

const NameEditor = ({ field, setConfig }) => (
  <ToggleRow
    first
    label="Middle name"
    sub="Add a middle-name box"
    on={!!field.config.middle}
    onChange={(middle) => setConfig('middle', middle)}
  />
);

NameEditor.propTypes = editorPropTypes;

const RatingEditor = ({ field, setConfig }) => (
  <Stepper
    label="Max stars"
    value={field.config.max}
    min={LIMITS.ratingMaxMin}
    max={LIMITS.ratingMaxMax}
    suffix="stars"
    onChange={(max) => setConfig('max', max)}
  />
);

RatingEditor.propTypes = editorPropTypes;

const columnsEditor = (field, setConfig, label) => (
  <ListEditor
    label={label}
    noun="column"
    items={field.config.columns}
    min={LIMITS.columnsMin}
    max={LIMITS.columnsMax}
    onChange={(columns) => setConfig('columns', columns)}
  />
);

const TableEditor = ({ field, setConfig }) => (
  <>
    {columnsEditor(field, setConfig, 'Columns')}
    <Stepper
      label="Starting rows"
      value={field.config.rows}
      min={LIMITS.tableRowsMin}
      max={LIMITS.tableRowsMax}
      suffix="rows"
      onChange={(rows) => setConfig('rows', rows)}
    />
  </>
);

TableEditor.propTypes = editorPropTypes;

const RepeaterEditor = ({ field, setConfig }) => (
  <>
    {columnsEditor(field, setConfig, 'Columns (asked for each entry)')}
    <TextField
      label="Add button label"
      value={field.config.add_label}
      placeholder="Add another"
      maxLength={LIMITS.addLabel}
      onChange={(value) => setConfig('add_label', value)}
    />
    <Stepper
      label="Max entries"
      value={field.config.max}
      min={LIMITS.repeaterMaxMin}
      max={LIMITS.repeaterMaxMax}
      suffix="entries"
      onChange={(max) => setConfig('max', max)}
    />
    <Hint>
      People start with one entry and tap the button to add more. Required means
      at least one entry.
    </Hint>
  </>
);

RepeaterEditor.propTypes = editorPropTypes;

const MatrixEditor = ({ field, setConfig }) => (
  <>
    <ListEditor
      label="Rows (things being rated)"
      noun="row"
      items={field.config.rows}
      min={LIMITS.matrixRowsMin}
      max={LIMITS.matrixRowsMax}
      onChange={(rows) => setConfig('rows', rows)}
    />
    <ListEditor
      label="Columns (rating choices)"
      noun="column"
      items={field.config.cols}
      min={LIMITS.matrixColsMin}
      max={LIMITS.matrixColsMax}
      onChange={(cols) => setConfig('cols', cols)}
    />
  </>
);

MatrixEditor.propTypes = editorPropTypes;

export const ANSWER_EDITORS = {
  dropdown: ChoiceEditor,
  radio: ChoiceEditor,
  checkbox: OptionsEditor,
  name: NameEditor,
  rating: RatingEditor,
  table: TableEditor,
  repeater: RepeaterEditor,
  matrix: MatrixEditor,
};
